import { useEffect, useState } from "react";
import { Check, ChevronsUpDown, Loader2, Search, UserRound } from "lucide-react";
import { listCampaignContacts, listCandidateContacts } from "@/api/email";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { useDebounce } from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";

export interface PickedContact {
  id: number;
  name: string;
  company: string | null;
  inCampaign: boolean;
}

/** "Preview for: [contact]" — the campaign's contacts first, then any
 *  contact of the project. */
export function ContactPicker({
  slug,
  campaignId,
  value,
  onChange,
}: {
  slug: string;
  campaignId: number;
  value: PickedContact | null;
  onChange: (contact: PickedContact) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debounced = useDebounce(query, 200);
  const [members, setMembers] = useState<PickedContact[]>([]);
  const [others, setOthers] = useState<PickedContact[]>([]);
  const [loading, setLoading] = useState(false);

  // Without a choice yet, take the campaign's first contact, else the project's.
  useEffect(() => {
    if (value) return;
    let cancelled = false;
    void (async () => {
      const inCampaign = await listCampaignContacts(slug, { campaign_id: campaignId, page_size: 1 }).catch(() => null);
      const first = inCampaign?.items[0];
      if (first && !cancelled) {
        onChange({ id: first.contact_id, name: first.contact_name, company: first.company_name, inCampaign: true });
        return;
      }
      const anyone = await listCandidateContacts(slug, { has_email: true, page_size: 1 }).catch(() => null);
      const candidate = anyone?.items[0];
      if (candidate && !cancelled) {
        onChange({ id: candidate.id, name: candidate.full_name, company: candidate.company_name, inCampaign: false });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- first pick only
  }, [slug, campaignId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      listCampaignContacts(slug, { campaign_id: campaignId, search: debounced, page_size: 8 }),
      listCandidateContacts(slug, { search: debounced, page_size: 12 }),
    ])
      .then(([inCampaign, project]) => {
        if (cancelled) return;
        const memberIds = new Set(inCampaign.items.map((m) => m.contact_id));
        setMembers(
          inCampaign.items.map((m) => ({ id: m.contact_id, name: m.contact_name, company: m.company_name, inCampaign: true })),
        );
        setOthers(
          project.items
            .filter((c) => !memberIds.has(c.id))
            .map((c) => ({ id: c.id, name: c.full_name, company: c.company_name, inCampaign: false })),
        );
      })
      .catch(() => undefined)
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, debounced, campaignId, slug]);

  const pick = (contact: PickedContact) => {
    onChange(contact);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-left shadow-sm-tile transition-colors hover:border-slate-300"
          aria-label="Preview for"
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
            <UserRound className="h-3.5 w-3.5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-slate-800">{value?.name ?? "Choose a contact"}</span>
            <span className="block truncate text-xs text-slate-500">
              {value ? (value.company ?? "No linked company") + (value.inCampaign ? " · in this campaign" : "") : "A real contact of the project"}
            </span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[340px] p-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a contact or a company…"
            className="h-8 rounded-lg border-slate-200 bg-slate-50/60 pl-8 text-xs"
          />
          {loading && <Loader2 className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-slate-400" />}
        </div>
        <div className="mt-2 max-h-[300px] space-y-2 overflow-y-auto">
          {[
            { label: "In this campaign", items: members },
            { label: "Project contacts", items: others },
          ].map(
            (group) =>
              group.items.length > 0 && (
                <div key={group.label}>
                  <p className="type-zonehead px-1.5 pb-1 text-[10px]">{group.label}</p>
                  {group.items.map((contact) => (
                    <button
                      key={`${group.label}-${contact.id}`}
                      type="button"
                      onClick={() => pick(contact)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-indigo-50/60",
                        value?.id === contact.id && "bg-indigo-50",
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-slate-800">{contact.name}</span>
                        <span className="block truncate text-xs text-slate-500">{contact.company ?? "No linked company"}</span>
                      </span>
                      {value?.id === contact.id && <Check className="h-4 w-4 text-indigo-600" />}
                    </button>
                  ))}
                </div>
              ),
          )}
          {!loading && !members.length && !others.length && (
            <p className="py-6 text-center text-xs text-slate-400">No contact found.</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
